import type { MetricsBreakdownRow } from '@dyspatch/core';
import { duration, formatUsd, percent } from './format';
import { applySort, byNumber, byText, type SortOption, type SortState } from './sorting';

/**
 * What a breakdown table's columns are, and what order its rows come out in.
 *
 * Nothing in here is React, for the same reason `sorting.ts` is not: the two
 * facts worth pinning - that the default order is merged PRs descending, and
 * that a column nothing measured parks its rows at the end rather than sorting
 * them as zero - are facts about the data, and a test can hold them still
 * without rendering a table.
 *
 * The comparators come from `sorting.ts` rather than from here. This file
 * declares the columns; that file decides what the URL means and how a list is
 * ordered, and there is only ever one of those in the console (issue #74).
 */

/** A `MetricsBreakdownRow` column: a sort key, a header and what the cell prints. */
export interface BreakdownColumn extends SortOption<MetricsBreakdownRow> {
  /** Right aligned and tabular. True for every column but the key. */
  numeric: boolean;
  /** What the cell shows. A dash where the row has nothing to show, never a zero. */
  cell: (row: MetricsBreakdownRow) => string;
  /**
   * Rows this column did not measure. They are held out of the comparison and
   * parked at the end in both directions, because "no PR merged here" is not a
   * lead time of zero and should not lead a table sorted by lead time - nor
   * trail one sorted the other way, as a null read as zero would.
   */
  unknown?: (row: MetricsBreakdownRow) => boolean;
}

/** The key column's sort value, which is also what the URL carries. */
export const BREAKDOWN_KEY_COLUMN = 'key';

/** The column the table opens on, descending. */
export const DEFAULT_BREAKDOWN_SORT = 'merged';

/**
 * A duration a person reads, or a dash. `duration(0)` is "just now", which is a
 * true thing to say about an instant and a false thing to say about a total, so
 * zero minutes prints as nothing measured instead.
 */
function minutesCell(minutes: number | null): string {
  if (minutes === null || minutes <= 0) return '-';
  return duration(minutes);
}

/**
 * Every column, left to right as the table draws them. `keyLabel` is the one
 * thing the two tables disagree about: the same row shape is a project on one
 * and a person on the other.
 */
export function breakdownColumns(keyLabel: string): BreakdownColumn[] {
  return [
    {
      value: BREAKDOWN_KEY_COLUMN,
      label: keyLabel,
      numeric: false,
      cell: (row) => row.key,
      compare: byText((row) => row.key),
      natural: 'asc',
    },
    {
      value: DEFAULT_BREAKDOWN_SORT,
      label: 'Merged',
      numeric: true,
      cell: (row) => String(row.mergedPrs),
      compare: byNumber((row) => row.mergedPrs),
      natural: 'desc',
    },
    {
      value: 'filed',
      label: 'Filed',
      numeric: true,
      cell: (row) => String(row.jobsFiled),
      compare: byNumber((row) => row.jobsFiled),
      natural: 'desc',
    },
    {
      value: 'ready',
      label: 'PRs ready',
      numeric: true,
      cell: (row) => String(row.prsReady),
      compare: byNumber((row) => row.prsReady),
      natural: 'desc',
    },
    {
      value: 'time',
      label: 'Agent time',
      numeric: true,
      cell: (row) => minutesCell(row.agentMinutes),
      compare: byNumber((row) => row.agentMinutes),
      natural: 'desc',
    },
    {
      value: 'cost',
      label: 'Cost',
      numeric: true,
      // Zero nanodollars is the sum of the runs that said what they cost, which
      // is nothing when none of them did (issue #18). "$0.00" would claim the
      // work was free.
      cell: (row) => (row.costNanoUsd > 0 ? formatUsd(row.costNanoUsd) : '-'),
      compare: byNumber((row) => row.costNanoUsd),
      natural: 'desc',
    },
    {
      value: 'merge',
      label: 'Filed to merge',
      numeric: true,
      cell: (row) => minutesCell(row.medianFiledToMergeMinutes),
      compare: byNumber((row) => row.medianFiledToMergeMinutes),
      natural: 'asc',
      unknown: (row) => row.medianFiledToMergeMinutes === null,
    },
    {
      value: 'first',
      label: 'First attempt',
      numeric: true,
      cell: (row) => (row.firstAttemptPassRate === null ? '-' : percent(row.firstAttemptPassRate)),
      compare: byNumber((row) => row.firstAttemptPassRate),
      natural: 'desc',
      unknown: (row) => row.firstAttemptPassRate === null,
    },
  ];
}

/**
 * The same columns as sort options, with merged PRs first so it is the default
 * (`defaultSort` in `sorting.ts` takes the head of the list). The table draws
 * them in `breakdownColumns` order; only the order of this array decides what a
 * reader who has picked nothing is looking at.
 */
export function breakdownSortOptions(columns: readonly BreakdownColumn[]): BreakdownColumn[] {
  const merged = columns.filter((column) => column.value === DEFAULT_BREAKDOWN_SORT);
  return [...merged, ...columns.filter((column) => column.value !== DEFAULT_BREAKDOWN_SORT)];
}

/**
 * The rows in the chosen order, with the rows that column could not measure
 * after them.
 *
 * The partition has to happen out here rather than inside a comparator:
 * `sortComparator` multiplies a comparator's answer by the direction, so a
 * comparator that puts nulls last ascending would put them first descending.
 * Holding them out and appending them keeps "unknown" at the end whichever way
 * the reader points the column, which is the only place it belongs - it is not
 * the best row and it is not the worst one.
 */
export function sortBreakdownRows(
  rows: readonly MetricsBreakdownRow[],
  options: readonly BreakdownColumn[],
  state: SortState,
): MetricsBreakdownRow[] {
  const id = (row: MetricsBreakdownRow) => row.key;
  const unknown = options.find((option) => option.value === state.key)?.unknown;
  if (!unknown) return applySort(rows, options, state, id);
  const measured = rows.filter((row) => !unknown(row));
  const missing = rows.filter((row) => unknown(row));
  return [
    ...applySort(measured, options, state, id),
    // By key, so the tail is in a stable order a refetch cannot reshuffle.
    ...applySort(missing, options, { key: BREAKDOWN_KEY_COLUMN, direction: 'asc' }, id),
  ];
}
